import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AccountSidebar from '../components/AccountSidebar';
import VoiceEnrollment from '../components/aura/VoiceEnrollment';

// Enhanced Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, loading, user }) => {
  if (!isOpen) return null;

  // Helper function to format plan name
  const formatPlanName = (plan) => {
    if (!plan) return '';
    return plan.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) + ' Plan';
  };

  // Dynamic warning message based on subscription status
  const getWarningMessage = () => {
    if (user?.subscription_plan) {
      const planName = formatPlanName(user.subscription_plan);
      return `This action is permanent and cannot be undone. Your active ${planName} subscription will be terminated immediately, and you will lose all access. To use TrendVector again, you will need to create a new account and purchase a new plan.`;
    } else {
      return "This action is permanent and cannot be undone. All of your data will be permanently deleted.";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        {/* Modal Header */}
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Are you absolutely sure?
          </h3>
          {user?.subscription_plan && (
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-sm font-medium text-red-600">
                Active Subscription Detected
              </span>
            </div>
          )}
        </div>

        {/* Warning Message */}
        <div className="mb-6">
          <p className="text-gray-700 leading-relaxed">
            {getWarningMessage()}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-medium"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </div>
            ) : (
              'Yes, Delete My Account'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Settings Content Component
const SettingsContent = () => {
  const { deleteAccount, user } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await deleteAccount();
      
      if (result.success) {
        // The logout will be handled by the deleteAccount function
        // and the user will be redirected to the login page
      } else {
        setMessage({ type: 'error', text: result.error });
        setShowDeleteModal(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
      setShowDeleteModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!loading) {
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="bg-white rounded-r-2xl p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Settings</h1>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Voice Verification Section */}
      <div className="mb-8">
        <VoiceEnrollment />
      </div>

      {/* Delete Account Section */}
      <div className="border border-gray-200 rounded-lg">
        <div className="flex justify-between items-center p-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Account</h3>
            <p className="text-sm text-gray-600 mt-1">
              Permanently delete your account and all associated data
            </p>
            {user?.subscription_plan && (
              <div className="mt-2 flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-sm text-red-600 font-medium">
                  This will also terminate your active subscription
                </span>
              </div>
            )}
          </div>
          <button
            onClick={handleDeleteClick}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Enhanced Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        loading={loading}
        user={user}
      />
    </div>
  );
};

// Main SettingsPage Component
const SettingsPage = () => {
  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: 'url(/assets/profilepage/profile_page_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Navbar Component */}
      <Navbar />
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-16 lg:py-12">
        <div className="max-w-5xl w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-4">
              {/* Sidebar - 1 column */}
              <AccountSidebar activePage="settings" />
              
              {/* Main Content - 3 columns */}
              <div className="md:col-span-3">
                <SettingsContent />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default SettingsPage;
